import { debug } from "debug";
import Subscriber from "../Subscriber";
import config from "../../config";
import { STORAGE_TRACE_KEY } from "../../types";
import pageManager from "../../page";
import { getTrace } from "../../store";

const logger = debug("vtTracker:exposeSubcriber");

const OBSERVE_QUERY = [
    "[vt-appid]",
    "[vt-itemid]",
    '[vt-expose="1"][vt-sbmdid]',
    '[vt-expose="1"][vt-mdid]'
];

const MAX_TIME = 2000;
const MIN_TIME = 1000;

const VISIBLE_THRESHOLD = 0.5;

export default class Expose extends Subscriber {
    private observer: IntersectionObserver;

    private observeList: Array<Element>;

    private updateTime = 0;

    private delayTimer: number;

    // private qbrwowserVisibilityState: string;

    private mutationObserver: MutationObserver;

    constructor() {
        super();

        this.observer = new IntersectionObserver(this.handle.bind(this), {
            threshold: VISIBLE_THRESHOLD
        });

        this.observeList = [];

        // this.qbrwowserVisibilityState = "visible";

        // if ((window as any).mqq) {
        //     (window as any).mqq.addEventListener(
        //         `qbrowserVisibilityChange`,
        //         (event: QbrowserVisibilityStateEvent) => {
        //             if (event.hidden) {
        //                 this.qbrwowserVisibilityState = "hidden";
        //             } else {
        //                 this.qbrwowserVisibilityState = "visible";
        //             }
        //         }
        //     );
        // }

        if (config.offAutoMutation !== true) {
            this.bodyMutation();
        }
    }

    private handle(entries: Array<IntersectionObserverEntry>): void {
        // 如果页面不可见，则停止对页面元素进行曝光上报
        // if (
        //     document.visibilityState === "hidden" ||
        //     this.qbrwowserVisibilityState === "hidden"
        // ) {
        //     return;
        // }
        if (pageManager.isDocumentVisible() === false) {
            return;
        }

        entries.forEach(it => {
            const rootHeight = it.rootBounds
                ? it.rootBounds.height
                : document.documentElement.offsetHeight;
            const visibleHeight = it.intersectionRect.height;

            // 元素的一半以上都在可视区域内；或者元素的可视区域充满了可视容器的一半以上
            // -----------------    -----------------
            // |               |    |               |
            // |               |    | ------------- |
            // |               |    | |           | |
            // |  -----------  |    | |           | |
            // |  |         |  |    | |           | |
            // |  |         |  |    | |           | |
            // |  |         |  |    | |           | |
            // ---|---------|---    --|-----------|--
            //    |         |         |           |
            //    -----------         |           |
            //                        |           |
            //                        |           |
            //                        |           |
            //                        |           |
            //                        |           |
            //                        -------------
            if (
                it.isIntersecting ||
                visibleHeight / rootHeight >= VISIBLE_THRESHOLD
            ) {
                Expose.dispatchExpose(it.target);
            } else {
                Expose.updatePageVisible(it.target, false);
            }
        });
    }

    static updatePageVisible(target: Element, visible: boolean): void {
        if (
            target.getAttribute("vt-pageid") ||
            document.documentElement.isSameNode(target)
        ) {
            pageManager.updatePageVisible(target, visible);
        }
    }

    static async dispatchExpose(target: Element): Promise<void> {
        // 是否是page曝光并且明确指定曝光上报前需要重新提取最新trace信息
        if (
            target.getAttribute("vt-pageid") &&
            target.getAttribute("vt-retrace")
        ) {
            logger("need to retrace before page expose %o", target);

            pageManager.storeTrace = await getTrace(STORAGE_TRACE_KEY);

            pageManager.recachePage(target);
        }

        Expose.updatePageVisible(target, true);

        target.dispatchEvent(
            new Event("vtExpose", {
                bubbles: true
            })
        );
    }

    private observeQuery(query: Array<string>): void {
        query.forEach(q => {
            document.querySelectorAll(q).forEach(it => {
                this.addObserve(it);
            });
        });
    }

    addObserve(el: Element): boolean {
        if (this.observeList.includes(el)) {
            return false;
        }

        this.observer.observe(el);

        this.observeList.push(el);

        return true;
    }

    updateObserve(force?: boolean): boolean {
        this.delayUpdate();

        const now = Date.now();

        if (force !== true && now - this.updateTime < MIN_TIME) {
            logger("limit by update frequency");

            return false;
        }

        this.updateTime = now;

        this.observeQuery(OBSERVE_QUERY);

        return true;
    }

    private delayUpdate(): void {
        // 需要在document.body上开启
        if (config.observe === false) {
            return;
        }

        if (window["requestIdleCallback"]) {
            this.delayTimer = window["requestIdleCallback"](
                () => {
                    this.updateObserve();
                },
                {
                    timeout: MAX_TIME
                }
            );
        } else {
            this.delayTimer = window.setTimeout(() => {
                this.updateObserve();
            }, MAX_TIME);
        }
    }

    setup(): void {
        document.documentElement.addEventListener("vtExpose", (e: Event) => {
            this.emit("action", e);
        });

        this.updateObserve(true);
    }

    stopUpdate(): void {
        this.mutationObserver.disconnect();

        if (!this.delayTimer) {
            return;
        }

        if (window["cancelIdleCallback"]) {
            window["cancelIdleCallback"](this.delayTimer);
        } else {
            window.clearTimeout(this.delayTimer);
        }

        this.delayTimer = 0;
    }

    get allObserved(): Array<Element> {
        return this.observeList;
    }

    private bodyMutation(): void {
        this.mutationObserver = new MutationObserver(
            this.mutationHandler.bind(this)
        );

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    private mutationHandler(): void {
        logger("detect body child tree changed");

        this.updateObserve(true);
    }
}
